import { create } from 'zustand'
import * as api from '../services/api'
import { VersionConflictError, startTimer, stopTimer, getActiveTimer } from '../services/api'

const useCommentStore = create((set, get) => ({
  // Comments organized by task ID
  commentsByTask: {},

  // Track which tasks are currently loading
  loadingTasks: new Set(),

  // Error state
  error: null,

  // Version conflict state
  versionConflict: null,

  // Active timer state
  activeTimer: null,  // { entry, task, projectId } or null
  activeTimerLoading: false,

  // Fetch comments for a specific task
  fetchComments: async (projectId, taskId) => {
    const taskKey = `${projectId}-${taskId}`
    // Add taskKey to loading set
    set(state => ({
      loadingTasks: new Set([...state.loadingTasks, taskKey])
    }))

    try {
      const comments = await api.fetchComments(projectId, taskId)
      set(state => {
        const newLoadingTasks = new Set(state.loadingTasks)
        newLoadingTasks.delete(taskKey)
        return {
          commentsByTask: {
            ...state.commentsByTask,
            [taskKey]: comments
          },
          loadingTasks: newLoadingTasks,
          error: null
        }
      })
      return comments
    } catch (error) {
      set(state => {
        const newLoadingTasks = new Set(state.loadingTasks)
        newLoadingTasks.delete(taskKey)
        return {
          loadingTasks: newLoadingTasks,
          error: error.message
        }
      })
      throw error
    }
  },

  // Add a new comment to a task
  addComment: async (projectId, taskId, content, durationSeconds = null) => {
    try {
      const comment = await api.addComment(projectId, taskId, content, durationSeconds)
      const taskKey = `${projectId}-${taskId}`
      set(state => ({
        commentsByTask: {
          ...state.commentsByTask,
          [taskKey]: [comment, ...(state.commentsByTask[taskKey] || [])]  // newest first
        },
        error: null
      }))
      return comment
    } catch (error) {
      set({ error: error.message })
      throw error
    }
  },

  // Update an existing comment
  updateComment: async (projectId, taskId, commentId, content, rowVersion) => {
    const taskKey = `${projectId}-${taskId}`
    try {
      const updatedComment = await api.updateComment(projectId, commentId, content, rowVersion)
      set(state => ({
        commentsByTask: {
          ...state.commentsByTask,
          [taskKey]: (state.commentsByTask[taskKey] || []).map(c =>
            c.id === commentId ? updatedComment : c
          )
        },
        error: null
      }))
      return updatedComment
    } catch (error) {
      if (error instanceof VersionConflictError) {
        // Refresh comments for this task
        await get().fetchComments(projectId, taskId)
        set({ versionConflict: { type: 'comment', message: error.message } })
        return null
      }
      set({ error: error.message })
      throw error
    }
  },

  // Delete a comment
  deleteComment: async (projectId, taskId, commentId, rowVersion) => {
    const taskKey = `${projectId}-${taskId}`
    try {
      await api.deleteComment(projectId, commentId, rowVersion)
      set(state => ({
        commentsByTask: {
          ...state.commentsByTask,
          [taskKey]: (state.commentsByTask[taskKey] || []).filter(c => c.id !== commentId)
        },
        error: null
      }))
    } catch (error) {
      if (error instanceof VersionConflictError) {
        // Refresh comments for this task
        await get().fetchComments(projectId, taskId)
        set({ versionConflict: { type: 'comment', message: error.message } })
        return
      }
      set({ error: error.message })
      throw error
    }
  },

  // Get comments for a task (synchronous)
  getComments: (projectId, taskId) => {
    const taskKey = `${projectId}-${taskId}`
    return get().commentsByTask[taskKey] || []
  },

  // Get comment count for a task (synchronous)
  getCommentCount: (projectId, taskId) => {
    const taskKey = `${projectId}-${taskId}`
    return (get().commentsByTask[taskKey] || []).length
  },

  // Check if a task's comments are loading
  isLoading: (projectId, taskId) => {
    const taskKey = `${projectId}-${taskId}`
    return get().loadingTasks.has(taskKey)
  },

  // Clear comments for a specific task (for cleanup)
  clearComments: (projectId, taskId) => {
    const taskKey = `${projectId}-${taskId}`
    set(state => {
      const { [taskKey]: _, ...rest } = state.commentsByTask
      return { commentsByTask: rest }
    })
  },

  // Clear all comments (e.g., on logout)
  clearAllComments: () => {
    set({
      commentsByTask: {},
      loadingTasks: new Set(),
      error: null,
      versionConflict: null,
      activeTimer: null,
      activeTimerLoading: false
    })
  },

  // Clear error
  clearError: () => set({ error: null }),

  // Clear version conflict
  clearVersionConflict: () => set({ versionConflict: null }),

  // Fetch active timer on app start
  fetchActiveTimer: async () => {
    try {
      set({ activeTimerLoading: true })
      const data = await getActiveTimer()
      if (data.entry) {
        set({
          activeTimer: {
            entry: data.entry,
            task: data.task,
            projectId: data.entry.projectId
          }
        })
      } else {
        set({ activeTimer: null })
      }
    } catch (error) {
      console.error('Failed to fetch active timer:', error)
    } finally {
      set({ activeTimerLoading: false })
    }
  },

  // Start timer on a task
  startTaskTimer: async (projectId, taskId) => {
    const state = get()
    if (state.activeTimer) {
      throw new Error('Timer already running')
    }

    try {
      const data = await startTimer(projectId, taskId)
      set({
        activeTimer: {
          entry: data.entry,
          task: data.task,
          projectId
        }
      })

      // Add to comments list for this task
      const taskKey = `${projectId}-${taskId}`
      const currentComments = state.commentsByTask[taskKey] || []
      set({
        commentsByTask: {
          ...state.commentsByTask,
          [taskKey]: [data.entry, ...currentComments]  // newest first
        }
      })

      return data.entry
    } catch (error) {
      if (error.message?.includes('409') || error.status === 409) {
        // Timer already active elsewhere - refresh to get current state
        await get().fetchActiveTimer()
      }
      throw error
    }
  },

  // Stop running timer
  stopTaskTimer: async (content = null) => {
    const state = get()
    if (!state.activeTimer) {
      throw new Error('No active timer')
    }

    const { entry, projectId } = state.activeTimer
    const taskId = entry.taskId

    try {
      const data = await stopTimer(projectId, entry.id, content, entry.rowVersion)

      // Update the entry in comments list
      const taskKey = `${projectId}-${taskId}`
      const currentComments = state.commentsByTask[taskKey] || []
      const updatedComments = currentComments.map(c =>
        c.id === entry.id ? data.entry : c
      )

      set({
        activeTimer: null,
        commentsByTask: {
          ...state.commentsByTask,
          [taskKey]: updatedComments
        }
      })

      return data.entry
    } catch (error) {
      if (error.name === 'VersionConflictError') {
        set({ versionConflict: { commentId: entry.id, serverData: error.serverData } })
      }
      throw error
    }
  },

  // Calculate current elapsed time from startedAt
  getElapsedSeconds: () => {
    const state = get()
    if (!state.activeTimer?.entry?.startedAt) return 0
    const startTime = new Date(state.activeTimer.entry.startedAt).getTime()
    return Math.floor((Date.now() - startTime) / 1000)
  },

  // Sum of durationSeconds for a task
  getTaskTotalTime: (projectId, taskId) => {
    const state = get()
    const taskKey = `${projectId}-${taskId}`
    const comments = state.commentsByTask[taskKey] || []
    return comments.reduce((total, comment) => {
      return total + (comment.durationSeconds || 0)
    }, 0)
  },

  // Clear timer state (for logout, etc)
  clearActiveTimer: () => {
    set({ activeTimer: null })
  }
}))

export default useCommentStore
